import type { StationMarkerKind } from '../types'

// Visual + grouping metadata for the 18 marker kinds. Single source of
// truth for the kind picker (groups + labels), the legend, and the
// pin colour-coding on the canvas.

export type KindGroup = 'facilities' | 'people' | 'items' | 'wayfinding'

interface KindMeta {
  label: string             // human-readable in the picker / legend
  group: KindGroup
  glyph: string             // single-char emoji / symbol shown inside the pin
  colour: string            // hex; matches the project palette where possible
}

// Kept inline rather than imported from a theme map so the colours
// stay readable in this one place. If the palette grows a "marker"
// token set later, swap to that.
export const KIND_META: Record<StationMarkerKind, KindMeta> = {
  // Permanent facilities (carried from mig 027) — slate-ish blues for
  // the "always there" colour family.
  'facility-toilets':    { label: 'Toilets',           group: 'facilities', glyph: '🚻', colour: '#3d5a6c' },
  'facility-first-aid':  { label: 'First aid',         group: 'facilities', glyph: '✚',  colour: '#7a2329' },
  'facility-defib':      { label: 'Defibrillator',     group: 'facilities', glyph: '⚡', colour: '#9b7f2a' },
  // People & info (mig 027) — green family for "team" colour identity.
  'staff-post':          { label: 'Staff post',        group: 'people',     glyph: '👤', colour: '#0a4a3a' },
  'photo-point':         { label: 'Photo point',       group: 'people',     glyph: '📷', colour: '#5a5a5a' },
  'hazard':              { label: 'Hazard',            group: 'people',     glyph: '⚠',  colour: '#c98a4e' },
  'comms-point':         { label: 'Comms point',       group: 'people',     glyph: '📡', colour: '#3d5a6c' },
  // Deployable items (mig 033) — brass family for "temporary kit."
  'item-portable-toilet':{ label: 'Portable toilet',   group: 'items',      glyph: '🚽', colour: '#9b7f2a' },
  'item-generator':      { label: 'Generator',         group: 'items',      glyph: '⛽', colour: '#9b7f2a' },
  'item-signage':        { label: 'Signage',           group: 'items',      glyph: '🪧', colour: '#9b7f2a' },
  'item-food-stall':     { label: 'Food / drink',      group: 'items',      glyph: '🍴', colour: '#9b7f2a' },
  'item-marquee':        { label: 'Marquee',           group: 'items',      glyph: '⛺', colour: '#9b7f2a' },
  'item-fencing':        { label: 'Fencing / barrier', group: 'items',      glyph: '🚧', colour: '#9b7f2a' },
  'item-bin':            { label: 'Bin',               group: 'items',      glyph: '🗑', colour: '#9b7f2a' },
  // Zones & wayfinding (mig 033) — oxblood for "movement / safety."
  'parking-zone':        { label: 'Parking zone',      group: 'wayfinding', glyph: 'P',  colour: '#3d5a6c' },
  'entrance':            { label: 'Entrance',          group: 'wayfinding', glyph: '→',  colour: '#0a4a3a' },
  'exit-emergency':      { label: 'Emergency exit',    group: 'wayfinding', glyph: '🚪', colour: '#7a2329' },
  'meeting-point':       { label: 'Meeting point',     group: 'wayfinding', glyph: '★',  colour: '#0a4a3a' }
}

export const GROUP_LABELS: Record<KindGroup, string> = {
  facilities: 'Facilities (permanent)',
  people:     'People & info',
  items:      'Deployable items',
  wayfinding: 'Zones & wayfinding'
}

export const GROUP_ORDER: KindGroup[] = ['facilities', 'people', 'items', 'wayfinding']
